import { Field, Label, Radio, RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import React, { useCallback, useEffect } from 'react';

import { useFilterContext } from '../lib';

const publicationTypes = ['all', 'продажа', 'дарение', 'случка'] as const;
const publicationTypesMap: Record<(typeof publicationTypes)[number], string> = {
  all: 'Все',
  продажа: 'Продажа',
  дарение: 'Дарение',
  случка: 'Вязка'
};

interface Props {
  urlSearchParams: URLSearchParams;
}

const PublicationType: React.FC<Props> = ({ urlSearchParams }) => {
  const { pubType, setPubType } = useFilterContext();
  const param = urlSearchParams.get('publication-type') as
    | 'all'
    | 'продажа'
    | 'дарение'
    | 'случка'
    | null;

  const updateUrl = useCallback(
    (value: (typeof publicationTypes)[number]) => {
      urlSearchParams.set('publication-type', value);
    },
    [urlSearchParams]
  );

  const changeRadioSelectHandle = useCallback(
    (value: (typeof publicationTypes)[number]) => {
      setPubType(value);
      sessionStorage.setItem('publication-type', value);
      updateUrl(value);
    },
    [setPubType, updateUrl]
  );

  useEffect(() => {
    const savedType = sessionStorage.getItem('publication-type') as
      | (typeof publicationTypes)[number]
      | undefined;
    if (!savedType) {
      sessionStorage.setItem('publication-type', pubType as string);
    } else {
      updateUrl(savedType);
    }
    setPubType(savedType ?? pubType);
  }, [pubType, setPubType, updateUrl]);

  useEffect(() => {
    if (param) {
      changeRadioSelectHandle(param);
    }
  }, [changeRadioSelectHandle, param, setPubType]);

  return (
    <RadioGroup
      value={pubType}
      onChange={changeRadioSelectHandle}
      aria-label="publication type"
      className="grid grid-cols-2 gap-2"
    >
      {publicationTypes.map((type) => (
        <Field as="div" key={type} className="flex items-center gap-1">
          <Radio
            value={type}
            className="group flex size-5 items-center justify-center rounded-full border border-gray-600 bg-white data-[checked]:bg-green-400 dark:data-[checked]:bg-green-600"
          >
            <CheckCircleIcon className="size-10 fill-white opacity-0 transition group-data-[checked]:opacity-100" />
          </Radio>
          <Label>{publicationTypesMap[type]}</Label>
        </Field>
      ))}
    </RadioGroup>
  );
};

export default PublicationType;
